import { useEffect, useState } from 'react';
import { pmGet, pmPost, type PmProject } from '../api';
import './StreamDefinition.css';
import { SelectField, PROMPT } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * Stream Definition — the delivery streams a programme runs, and who staffs each one.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/streams.ejs`.
 * The source's own definition, from its help panel:
 *
 *   A stream is a parallel workstream organized around a business process. Splitting
 *   delivery by function lets each stream own its work items, assignments and RACI —
 *   rolling up into the same project timeline.
 *
 * THE CATALOGUE IS GLOBAL, THE SELECTION IS PER PROJECT — and the screen is blunt about it.
 * Adding or removing a stream, or a role, changes the catalogue for EVERY project, which is
 * why both confirmations say so out loud. Ticking a stream in or out is the per-project act
 * and only takes effect on Save.
 *
 * A DE-SELECTED STREAM IS DIMMED, NOT HIDDEN. It keeps its members, and the save is a
 * non-destructive upsert keyed by name, so unticking a stream never discards the team that
 * was assembled on it.
 *
 * THE MEMBER LIST IS THE ROSTER THE REST OF THE PRODUCT READS. Work Item Assignment offers
 * these people, and Settings → Project Visibility bridges them to logins by email — which
 * is why an email typed here matters beyond this screen.
 */

interface StreamMember {
  name: string;
  email: string;
  role: string;
}

interface Stream {
  /** Client-side row id. The source calls it `_sid`; streams have no id until saved. */
  _sid: number;
  name: string;
  abbrev: string | null;
  is_custom: boolean;
  in_scope: boolean;
  members: StreamMember[];
}

interface StreamsData {
  success: boolean;
  error?: string;
  project: PmProject;
  streams: Omit<Stream, '_sid'>[];
  roles: string[];
  customRoles: string[];
}

/** A member counts once they have a name or an email — a blank row does not. */
const memberCount = (s: Stream) =>
  (s.members ?? []).filter((m) => (m.name || '').trim() || (m.email || '').trim()).length;

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/streams.ejs`,
 * its `streamsHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const STREAMS_HELP: HelpContent = {
    title: 'Stream Definition',
    icon: '🌳',
    purpose: "Define the <strong>business-function streams</strong> a project is delivered along &mdash; e.g. <em>O2C</em> (Order-to-Cash), <em>P2P</em> (Procure-to-Pay), <em>R2R</em> (Record-to-Report) &mdash; and the <strong>team members</strong> staffing each one.",
    doHere: [
      "Select a <strong>project</strong>; its methodology pill (Waterfall / Agile) shows for context.",
      "Use the <strong>In project / All streams</strong> toggle above the list. <strong>In project</strong> (the opening view) shows only the streams this project runs; <strong>All streams</strong> shows the full function set so you can bring another one in.",
      "Add or remove <strong>streams</strong> in the left panel &mdash; pick from the standard function set.",
      "For the selected stream, add <strong>members</strong> (name, email, role) on the right, including customer-side roles.",
      "Click <strong>Save Streams</strong> to persist the stream &amp; team structure for this project."
    ],
    fits: 'Project Timeline Finalization &rarr; <strong>Stream Definition</strong> &rarr; Work Item Definition &rarr; Work Item Assignment',
    firstTime: "Pick a project, add a stream (e.g. <strong>O2C</strong>), add its members on the right, then <strong>Save Streams</strong>.",
    sections: [
      {
        label: 'What a stream is',
        type: 'html',
        html: "A <strong>stream</strong> is a parallel workstream organized around a business process. Splitting delivery by function lets each stream own its work items, assignments and RACI &mdash; rolling up into the same project timeline."
      },
      {
        label: 'The two stream views',
        type: 'defs',
        items: [
          { term: 'In project', color: '#1976d2', def: "Only the streams this project has taken on &mdash; the plan as it stands. This is where the screen opens, because reading the plan is the common case. Un&#8209;tick a stream here and it leaves the list immediately." },
          { term: 'All streams', color: '#546e7a', def: "The full catalog of business functions to choose from, ticked or not. Switch here to bring a new stream into the project." }
        ],
        note: "Either view can be edited, and <strong>members</strong> are attached to the stream itself &mdash; so a stream keeps its people whichever view you added them from."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/project_management/user-guide#work-items' },
      { label: 'Next: Work Item Definition →', href: '/project_management/program-management/scope-ownership/work-item-definition' }
    ]
  };

export function StreamDefinition() {
  const [projects, setProjects] = useState<PmProject[]>([]);
  const [projectId, setProjectId] = useState('');
  const [project, setProject] = useState<PmProject | null>(null);
  const [streams, setStreams] = useState<Stream[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [customRoles, setCustomRoles] = useState<string[]>([]);
  const [selSid, setSelSid] = useState<number | null>(null);
  /*
   * VIEW TOGGLE — §7.3.
   *
   *   active   only the streams THIS project runs   (default)
   *   all      the full catalogue, to pick from
   *
   * It opens on "In project" because READING the plan is the common case; adding a stream is
   * not. That is the opposite default to Work Item Definition's scope toggle, and deliberately
   * so — you arrive at Work Item Definition to choose, and here to check.
   */
  const [streamView, setStreamView] = useState<'active' | 'all'>('active');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    pmGet<{ projects: PmProject[] }>('/deliverables/projects')
      .then((d) => setProjects(d.projects ?? []))
      .catch((e) => setError((e as Error).message));
  }, []);

  const onSelectProject = async (id: string) => {
    setProjectId(id);
    setError(null);
    setNotice(null);
    if (!id) return;
    try {
      const d = await pmGet<StreamsData>(
        `/streams/data?delivery_project_id=${encodeURIComponent(id)}`,
      );
      if (!d.success) {
        setError(d.error ?? 'failed');
        return;
      }
      setProject(d.project);
      setRoles(d.roles ?? []);
      setCustomRoles(d.customRoles ?? []);
      const next = (d.streams ?? []).map((s, i) => ({
        ...s,
        _sid: i,
        members: (s.members ?? []).map((m) => ({
          name: m.name || '',
          email: m.email || '',
          role: m.role || '',
        })),
      }));
      setStreams(next);
      // auto-select first in-scope stream
      const first = next.find((s) => s.in_scope) ?? next[0];
      setSelSid(first ? first._sid : null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const patch = (sid: number, fn: (s: Stream) => Stream) =>
    setStreams((prev) => prev.map((s) => (s._sid === sid ? fn({ ...s }) : s)));

  const addStream = async () => {
    const name = (window.prompt('New stream name (added to the catalog for ALL projects):') || '').trim();
    if (!name) return;
    if (streams.some((s) => s.name.toLowerCase() === name.toLowerCase())) {
      setError('That stream already exists.');
      return;
    }
    const abbrev = (window.prompt('Short code (optional, e.g., "TRY"):') || '').trim();
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/streams/catalog/add', {
        name,
        abbrev,
      });
      if (!d.success) {
        setError(d.error ?? 'failed');
        return;
      }
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    const sid = streams.length ? Math.max(...streams.map((s) => s._sid)) + 1 : 0;
    setStreams((prev) => [
      ...prev,
      { _sid: sid, name, abbrev, is_custom: true, in_scope: true, members: [] },
    ]);
    setSelSid(sid);
  };

  const removeStream = async (sid: number) => {
    const s = streams.find((x) => x._sid === sid);
    if (!s) return;
    if (
      !window.confirm(`Remove stream "${s.name}" from the catalog for ALL projects?`)
    )
      return;
    try {
      await pmPost('/streams/catalog/remove', { name: s.name });
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    setStreams((prev) => prev.filter((x) => x._sid !== sid));
    if (selSid === sid) setSelSid(null);
  };

  const addRole = async () => {
    const role = (window.prompt('New role name (added for ALL projects):') || '').trim();
    if (!role) return;
    if (roles.includes(role)) {
      setError('That role already exists.');
      return;
    }
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/streams/role/add', { role });
      if (d.success) {
        setRoles((prev) => [...prev, role]);
        setCustomRoles((prev) => [...prev, role]);
      } else setError(d.error ?? 'failed');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const removeRole = async (role: string) => {
    if (
      !window.confirm(
        `Remove role "${role}" from the list for ALL projects? (Members already tagged keep the text.)`,
      )
    )
      return;
    try {
      await pmPost('/streams/role/remove', { role });
      setRoles((prev) => prev.filter((r) => r !== role));
      setCustomRoles((prev) => prev.filter((r) => r !== role));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const save = async () => {
    if (!project) return;
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/streams/save', {
        delivery_project_id: project.delivery_project_id,
        streams: streams.map((s) => ({
          name: s.name,
          abbrev: s.abbrev,
          in_scope: !!s.in_scope,
          is_custom: !!s.is_custom,
          members: s.members,
        })),
      });
      if (d.success) setNotice('Streams saved.');
      else setError(d.error ?? 'failed');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const sel = selSid != null ? (streams.find((s) => s._sid === selSid) ?? null) : null;
  const inScopeCount = streams.filter((s) => s.in_scope).length;
  const agile = project?.methodology === 'Agile';

  return (
    <div className="pm-stp">
      <HelpPanel help={withFinalizedRule(STREAMS_HELP)} />
      <div className="st-head">
        <div className="st-titlerow">
          <i className="material-icons" style={{ fontSize: '2rem', color: '#1976d2' }}>
            account_tree
          </i>
          <h1 className="page-header__title">Stream Definition</h1>
        </div>
        <div className="st-toolbar">
          <SelectField
            label="Project"
            width="lg"
            value={projectId}
            onChange={(v) => void onSelectProject(v)}
            placeholder={PROMPT.project}
            options={projects.map((p) => ({
              value: p.delivery_project_id,
              id: p.delivery_project_id,
              description: `${p.description ?? ''}${p.methodology ? ` (${p.methodology})` : ''}`.trim(),
            }))}
          />
          {project ? (
            <div className="meth-switch">
              <span
                className="meth-pill"
                style={{ background: agile ? '#fff' : '#1565c0', color: agile ? '#b0bec5' : '#fff' }}
              >
                Waterfall
              </span>
              <span
                className="meth-pill"
                style={{
                  borderLeft: '1px solid #cfd8dc',
                  background: agile ? '#2e7d32' : '#fff',
                  color: agile ? '#fff' : '#b0bec5',
                }}
              >
                Agile
              </span>
            </div>
          ) : null}
          <button
            className="btn-primary"
            style={{ marginLeft: 'auto' }}
            disabled={!project}
            onClick={() => void save()}
          >
            Save Streams
          </button>
        </div>
        {error ? <div style={{ color: '#c62828', fontSize: 12.5, marginTop: 6 }}>{error}</div> : null}
        {notice ? (
          <div style={{ color: '#2e7d32', fontSize: 12.5, marginTop: 6 }}>{notice}</div>
        ) : null}
      </div>

      <div className="st-main">
        <div className="st-left">
          {!project ? (
            <div style={{ padding: '48px 12px', textAlign: 'center', color: '#90a4ae' }}>
              <i className="material-icons" style={{ fontSize: 48, color: '#cfd8dc' }}>
                account_tree
              </i>
              <p style={{ margin: '10px 0 0' }}>Select a project to load its streams.</p>
            </div>
          ) : (
            <>
              <div className="st-sub" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span>
                  Streams <span style={{ color: '#1976d2' }}>· {inScopeCount} in project</span>
                </span>
                {/* The same segmented control the methodology toggle uses — working
                    agreement 6: reuse the existing styling so the gesture is learned once. */}
                <span className="mth-tog" style={{ marginLeft: 'auto' }}>
                  <button
                    type="button"
                    className={`mth wf${streamView === 'active' ? ' on' : ''}`}
                    title="Only the streams this project runs"
                    onClick={() => setStreamView('active')}
                  >
                    In project
                  </button>
                  <button
                    type="button"
                    className={`mth wf${streamView === 'all' ? ' on' : ''}`}
                    title="The full catalogue, to pick from"
                    onClick={() => setStreamView('all')}
                  >
                    All streams
                  </button>
                </span>
              </div>
              {/*
                In the "In project" view a stream that is not in scope does not belong in the
                list at all. Un-ticking one therefore REMOVES it from this view rather than
                leaving a greyed row the view says should not be there — §7.3's own note.
              */}
              {streams.filter((s) => streamView === 'all' || s.in_scope).length === 0 ? (
                <div style={{ padding: '18px 6px', color: '#90a4ae', fontSize: 13 }}>
                  {streamView === 'active'
                    ? 'No streams are in this project yet — switch to All streams to add some.'
                    : 'No streams in the catalogue yet.'}
                </div>
              ) : null}
              {streams.filter((s) => streamView === 'all' || s.in_scope).map((s) => {
                const n = memberCount(s);
                return (
                  <div
                    key={s._sid}
                    className={`strow ${s._sid === selSid ? 'sel' : ''} ${s.in_scope ? '' : 'off'}`}
                    onClick={() => setSelSid(s._sid)}
                  >
                    <input
                      type="checkbox"
                      checked={s.in_scope}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => patch(s._sid, (x) => ({ ...x, in_scope: e.target.checked }))}
                    />
                    {s.abbrev ? (
                      <span className="st-abbr">{s.abbrev}</span>
                    ) : (
                      <span className="st-abbr" style={{ background: '#f3e5f5', color: '#8e24aa' }}>
                        NEW
                      </span>
                    )}
                    <span className="st-name">{s.name}</span>
                    <span className="st-mcount">{n ? `${n} 👤` : ''}</span>
                    {s.is_custom ? (
                      <span
                        className="st-rm"
                        title="Remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          void removeStream(s._sid);
                        }}
                      >
                        ✕
                      </span>
                    ) : null}
                  </div>
                );
              })}
              <button className="st-add" onClick={() => void addStream()}>
                + Add stream
              </button>
            </>
          )}
        </div>

        <div className="st-right">
          {!sel ? (
            <div style={{ padding: '56px 16px', textAlign: 'center', color: '#90a4ae' }}>
              <i className="material-icons" style={{ fontSize: 54, color: '#cfd8dc' }}>
                groups
              </i>
              <p style={{ margin: '12px 0 0' }}>
                {project
                  ? 'Select a stream on the left to manage its team members.'
                  : 'Pick a project, then select a stream to manage its team members.'}
              </p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                {sel.abbrev ? <span className="st-abbr">{sel.abbrev}</span> : null}
                <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 600, color: '#263238' }}>
                  {sel.name}
                </h2>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: sel.in_scope ? '#2e7d32' : '#90a4ae',
                    background: sel.in_scope ? '#e8f5e9' : '#eceff1',
                    padding: '3px 10px',
                    borderRadius: 11,
                  }}
                >
                  {sel.in_scope ? 'In project' : 'Not selected'}
                </span>
              </div>
              <p style={{ color: '#90a4ae', fontSize: 12, margin: '0 0 12px' }}>
                Team members — name, email and role for this stream.
              </p>

              <div className="roles-bar">
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: '#90a4ae',
                  }}
                >
                  Roles
                </span>
                {roles.map((r) => {
                  const c = customRoles.includes(r);
                  return (
                    <span className={`role-chip ${c ? 'cust' : ''}`} key={r}>
                      {r}
                      {c ? (
                        <span className="role-x" onClick={() => void removeRole(r)}>
                          {' '}
                          ✕
                        </span>
                      ) : null}
                    </span>
                  );
                })}
                <button className="role-addbtn" onClick={() => void addRole()}>
                  + Add role
                </button>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: '#b0bec5',
                  padding: '0 2px 4px',
                }}
              >
                <span style={{ width: 190 }}>Name</span>
                <span style={{ flex: 1, minWidth: 180 }}>Email</span>
                <span style={{ width: 200 }}>Role</span>
                <span style={{ width: 14 }} />
              </div>

              {sel.members.length ? (
                sel.members.map((m, mi) => (
                  <div className="mem-row" key={mi}>
                    <input
                      type="text"
                      className="mem-in nm"
                      placeholder="Full name"
                      value={m.name}
                      onChange={(e) =>
                        patch(sel._sid, (x) => {
                          x.members = x.members.map((mm, i) =>
                            i === mi ? { ...mm, name: e.target.value } : mm,
                          );
                          return x;
                        })
                      }
                    />
                    <input
                      type="email"
                      className="mem-in em"
                      placeholder="email@company.com"
                      value={m.email}
                      onChange={(e) =>
                        patch(sel._sid, (x) => {
                          x.members = x.members.map((mm, i) =>
                            i === mi ? { ...mm, email: e.target.value } : mm,
                          );
                          return x;
                        })
                      }
                    />
                    <input
                      type="text"
                      className="mem-in rl"
                      list="pm-stp-roles"
                      placeholder="Role"
                      value={m.role}
                      onChange={(e) =>
                        patch(sel._sid, (x) => {
                          x.members = x.members.map((mm, i) =>
                            i === mi ? { ...mm, role: e.target.value } : mm,
                          );
                          return x;
                        })
                      }
                    />
                    <span
                      className="mem-rm"
                      title="Remove"
                      onClick={() =>
                        patch(sel._sid, (x) => {
                          x.members = x.members.filter((_, i) => i !== mi);
                          return x;
                        })
                      }
                    >
                      ✕
                    </span>
                  </div>
                ))
              ) : (
                <div style={{ color: '#b0bec5', fontSize: 13, marginBottom: 8 }}>
                  No team members yet.
                </div>
              )}

              {/* The role field is a free-text input WITH a datalist, not a select: a role
                  that is not in the catalogue is still typable, which is what lets the
                  catalogue stay short. */}
              <datalist id="pm-stp-roles">
                {roles.map((r) => (
                  <option value={r} key={r} />
                ))}
              </datalist>

              <button
                className="mem-add"
                style={{ marginTop: 6 }}
                onClick={() =>
                  patch(sel._sid, (x) => {
                    x.members = [...x.members, { name: '', email: '', role: '' }];
                    return x;
                  })
                }
              >
                + Add member
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
